import { Injectable, OnDestroy } from '@angular/core';

import { Subscription } from 'rxjs';

import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { BlockModel } from '../../../Common/PageBuilder/models/block.model';
import { chartColors } from '../../../Common/Charts/enums/chart-colors.enum';
import { ChartDisplayElementModel } from '../models/chart-display-element.model';
import { ChartMetricsEnum } from '../enums/chart-metrics.enum';
import { ChartSeries } from '../../Charts/models/chart-series.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { mediaTypeToPieMap } from './reports-series-mapper.service';
import { ReportBlockTypeEnum, reportBlockTypeEnumToDefaultChartSpecificType } from '../enums/report-block-type.enum';
import { ReportSeriesModel } from '../models/report-series.model';
import { UserCurrency } from '../../../Core/Users/enums/user-currency';
import { ReportsChartOptionsService } from './reports-chart-options.service';
import { ChartSpecificTypesEnum } from '../enums/chart-specific-types.enum';
import { ExternalChartMetricsEnum } from '../enums/external-chart-metrics.enum';
import { GoogleAnalyticsReportTypesEnum } from '../../../api/externalData/enums/google-analytics-report-types.enum';
import { sortSentiment } from '../../../Common/Misc/helpers/sort/sort.helper';
import { MediaTypeEnum } from '../../Basic/enums/media-type.enum';
import { StackedChartTypesEnum } from '../enums/stacked-chart-types.enum';


@Injectable()
export class ReportsChartSeriesConverterService implements OnDestroy {

    currency: UserCurrency;
    subscriptions: Subscription[] = [];

    constructor(
        public currentUserStateService: CurrentUserStateService,
        public localizationService: LocalizationService,
        public reportsChartOptionsService: ReportsChartOptionsService
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getUserCurrency().subscribe(x => this.currency = x)
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x.unsubscribe());
    }

    public getChartSeries(reportSeries: ReportSeriesModel[], block: BlockModel): ChartSeries[] {
        switch (block.type.key) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
            case ReportBlockTypeEnum.PROMINENCE_OVER_TIME:
                return this.getDateValueChartSeriesWithBlock(reportSeries, block);
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
                return this.getStackedColumnChartSeriesWithBlock(block);
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
                return this.getCategoryBreakdownChartSeries(reportSeries, block);
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
                return this.getPreviousPeriodChartSeries(reportSeries, block);
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                return this.getSentimentDateValueChartSeriesWithBlock(reportSeries, block);
            case ReportBlockTypeEnum.TOP_REFERRAL_SOURCES:
                return this.getTopReferralSourcesChartSeries(reportSeries, block);
            case ReportBlockTypeEnum.REFERRAL_TREND:
                return this.getReferralTrendChartSeries(reportSeries, block);
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                return this.getCoverageImpactTrendChartSeries(reportSeries, block);
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_HASHTAGS:
                return [{ colorMapKey: 'seriesColor0' }];
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS:
                return [{ colorMapKey: 'barColor' }];
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                return [{}];
            case ReportBlockTypeEnum.COVERAGE_METRIC:
                return this.getCoverageMetricChartSeries(reportSeries[0]);
        }

        return [];
    }

    public getDateValueChartSeriesWithBlock(reportSeries: ReportSeriesModel[], block: BlockModel): ChartSeries[] {
        const displayElement = block.displayElement as ChartDisplayElementModel;
        return this.getDateValueChartSeries(
            reportSeries, block.type.key as ReportBlockTypeEnum, displayElement.chartSpecificType, displayElement.styles?.currency
        );
    }

    public getDateValueChartSeries(
        reportSeries: ReportSeriesModel[],
        blockTypeKey: ReportBlockTypeEnum,
        chartSpecificType: ChartSpecificTypesEnum,
        currency: string,
        hideExtraAxisGrids: boolean = false
    ): ChartSeries[] {
        if (!reportSeries.length) {
            return [];
        }
        const chartSeries = [];
        let existingAxis = -1;
        chartSpecificType = chartSpecificType || reportBlockTypeEnumToDefaultChartSpecificType.get(blockTypeKey);
        if (reportSeries[0]?.metrics?.length > 0) {
            const s = reportSeries[0];
            reportSeries[0].metrics.forEach((metric, index) => {
                const series = {} as ChartSeries;
                series.chartSpecificType = chartSpecificType;
                series.key = metric;
                series.label = this.localizationService.transform(`Reports.analysis.type.${metric}`); // if multiple series, then should be source name
                series.exportLabel = this.localizationService.transform(`Reports.analysis.type.${metric}`);
                if (metric === ChartMetricsEnum.ave) {
                    series.label += ` (${currency ?? this.currency})`;
                    series.exportLabel += ` (${currency ?? this.currency})`;
                }
                series.requiresAxis = true;
                if (series.requiresAxis) {
                    existingAxis += 1;
                }
                series.axisIndex = existingAxis;
                if (hideExtraAxisGrids && existingAxis !== 0) {
                    series.hideAxisGrid = true;
                }
                if (metric === ChartMetricsEnum.ENGAGEMENT_RATE) {
                    series.percentageValueAxis = true;
                }
                series.colorMapKey = 'analysisColor' + (index + 1);
                series.getSeriesUrl = s.fullSearch ?
                    (<ReportSeriesModel>s).getSocialSeriesUrl.bind(s) : (<ReportSeriesModel>s).getMentionSeriesUrl.bind(s);
                series.prominenceTrendGroup = (<ReportSeriesModel>s).prominenceTrendGroup;
                chartSeries.push(series);
            });
        } else {
            reportSeries.forEach((s, index) => {
                const series = {} as ChartSeries;
                series.chartSpecificType = chartSpecificType;
                series.key = s.metric;
                series.label = s.label;
                series.exportLabel = s.label + ' - ' + this.localizationService.transform(`Reports.analysis.type.${s.metric}`);
                series.requiresAxis = index === 0;
                series.prominenceTrendGroup = (<ReportSeriesModel>s).prominenceTrendGroup;
                if (series.requiresAxis) {
                    existingAxis += 1;
                }
                series.axisIndex = existingAxis;
                series.colorMapKey = 'seriesColor' + index;
                series.getSeriesUrl = s instanceof ReportSeriesModel && (<ReportSeriesModel>s).fullSearch ?
                    (<ReportSeriesModel>s).getSocialSeriesUrl.bind(s) : (<ReportSeriesModel>s).getMentionSeriesUrl.bind(s);
                chartSeries.push(series);
            });
        }

        return chartSeries;
    }

    public getSentimentDateValueChartSeriesWithBlock(reportSeries: ReportSeriesModel[], block: BlockModel): ChartSeries[] {
        const displayElement = block.displayElement as ChartDisplayElementModel;
        return this.getSentimentDateValueChartSeries(
            reportSeries,
            block.type.key as ReportBlockTypeEnum,
            displayElement.chartSpecificType,
            displayElement.stackedChartTypes
        );
    }

    public getSentimentDateValueChartSeries(
        reportSeries: ReportSeriesModel[],
        blockTypeKey: ReportBlockTypeEnum,
        chartSpecificType: ChartSpecificTypesEnum,
        stackedChartTypes = StackedChartTypesEnum.PERCENTAGE
    ): ChartSeries[] {
        if (!reportSeries.length) {
            return [];
        }
        const chartSeries = [];
        let existingAxis = -1;
        chartSpecificType = chartSpecificType || reportBlockTypeEnumToDefaultChartSpecificType.get(blockTypeKey);
        const s = reportSeries[0];
        sortSentiment(s.sentiments || []);
        s.sentiments.forEach((sentiment, index) => {
            const series = {} as ChartSeries;
            series.chartSpecificType = chartSpecificType;
            series.key = sentiment;
            series.colorMapKey = sentiment;
            series.label = this.localizationService.transform(`Sentiment.${sentiment}`);
            series.exportLabel = sentiment;
            series.requiresAxis = index === 0;
            if (series.requiresAxis) {
                existingAxis += 1;
            }
            series.axisIndex = existingAxis;
            series.getSeriesUrl = s.fullSearch ?
                (<ReportSeriesModel>s).getSocialSeriesUrl.bind(s) : (<ReportSeriesModel>s).getMentionSeriesUrl.bind(s);
            if (stackedChartTypes === StackedChartTypesEnum.PERCENTAGE) {
                series.percentageValueAxis = true;
            }
            series.prominenceTrendGroup = (<ReportSeriesModel>s).prominenceTrendGroup;
            chartSeries.push(series);
        });

        return chartSeries;
    }

    public getStackedColumnChartSeriesWithBlock(block: BlockModel): ChartSeries[] {
        return this.getStackedColumnChartSeries(
            block.type.key as ReportBlockTypeEnum,
            (block.displayElement as ChartDisplayElementModel).sentiments,
            (block.displayElement as ChartDisplayElementModel).networkSelection,
            (block.displayElement as ChartDisplayElementModel).series
        );
    }

    public getStackedColumnChartSeries(
        key: ReportBlockTypeEnum,
        sentiments?: SearchCriteriaQuerySentimentsEnum[],
        networkSelection?: MediaTypeEnum[],
        reportSeries?: ReportSeriesModel[]
    ): ChartSeries[] {
        const chartSeries = [];
        switch (key) {
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
            case ReportBlockTypeEnum.SENTIMENT_BY_MEDIA_TYPE:
                const sentimentsMap = new Map<SearchCriteriaQuerySentimentsEnum, ChartSeries>([
                    [
                        SearchCriteriaQuerySentimentsEnum.positive,
                        {
                            key: SearchCriteriaQuerySentimentsEnum.positive,
                            label: this.localizationService.transform(`Sentiment.${SearchCriteriaQuerySentimentsEnum.positive}`),
                            labelColor: chartColors.agprWhite
                        }
                    ],
                    [
                        SearchCriteriaQuerySentimentsEnum.neutral,
                        {
                            key: SearchCriteriaQuerySentimentsEnum.neutral,
                            label: this.localizationService.transform(`Sentiment.${SearchCriteriaQuerySentimentsEnum.neutral}`),
                            labelColor: chartColors.agprBlack
                        }
                    ],
                    [
                        SearchCriteriaQuerySentimentsEnum.negative,
                        {
                            key: SearchCriteriaQuerySentimentsEnum.negative,
                            label: this.localizationService.transform(`Sentiment.${SearchCriteriaQuerySentimentsEnum.negative}`),
                            labelColor: chartColors.agprWhite
                        }
                    ],                [
                        SearchCriteriaQuerySentimentsEnum.notAvailable,
                        {
                            key: SearchCriteriaQuerySentimentsEnum.notAvailable,
                            label: this.localizationService.transform(`Sentiment.${SearchCriteriaQuerySentimentsEnum.notAvailable}`),
                            labelColor: chartColors.agprBlack
                        }
                    ]
                ]);
                sentiments.forEach(sentiment => {
                    chartSeries.push(sentimentsMap.get(sentiment));
                });
                break;
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
                const mediaTypes = networkSelection ||  Object.values(MediaTypeEnum);
                mediaTypes.forEach(mediaType => {
                    const series = mediaTypeToPieMap.get(mediaType) as ChartSeries;
                    series.key = mediaType;
                    series.label = this.localizationService.transform(series.label);
                    chartSeries.push(series);
                });
                break;
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
                const useSubResourcColor = reportSeries.every(s => s.styles?.parentGroupId || s.styles?.parentThemeId);
                reportSeries.forEach((s, i) => {
                    chartSeries.push({
                        key: s.label,
                        label: s.label,
                        colorMapKey: useSubResourcColor ? 'subSource' + (i + 1) : 'seriesColor' + i
                    });
                });
                break;
        }

        return chartSeries;
    }

    public getCategoryBreakdownChartSeries(reportSeries: ReportSeriesModel[], block: BlockModel): ChartSeries[] {
        let chartSeries: ChartSeries[] = [];
        const chartSpecificType = (block.displayElement as ChartDisplayElementModel).chartSpecificType;
        if (chartSpecificType === ChartSpecificTypesEnum.PIE) {
            if (reportSeries.length === 1) {
                chartSeries.push({
                    metric: reportSeries[0].metric,
                    exportLabel: this.localizationService.transform('Reports.count'),
                    exportPercentLabel: this.localizationService.transform('Reports.percent'),
                    hiddenInLegend: false
                });
            }
        } else {
            if (
                chartSpecificType === ChartSpecificTypesEnum.BAR
                || chartSpecificType === ChartSpecificTypesEnum.COLUMN
            ) {
                chartSeries = [{}];
                if ((block.displayElement as ChartDisplayElementModel).chartHeaderSettings?.previousPeriod) {
                    chartSeries.push({});
                }
            } else {
                chartSeries = this.getStackedColumnChartSeriesWithBlock(block);
            }
        }

        return chartSeries;
    }

    public getPreviousPeriodChartSeries(reportSeries: ReportSeriesModel[], block: BlockModel): ChartSeries[] {
        let chartSeries: ChartSeries[] = [];
        const chartSpecificType = (block.displayElement as ChartDisplayElementModel).chartSpecificType;

        switch (chartSpecificType) {
            case ChartSpecificTypesEnum.PIE:
            case ChartSpecificTypesEnum.BAR:
            case ChartSpecificTypesEnum.COLUMN:
                if ((block.displayElement as ChartDisplayElementModel).chartHeaderSettings?.previousPeriod) {
                    chartSeries.push(
                        {
                            innerRadius: 25,
                            radius: 40,
                            exportLabel: this.reportsChartOptionsService.getLabelByMetric(
                                reportSeries[0].metric, block.displayElement as ChartDisplayElementModel)
                                + ' - ' + this.localizationService.transform('Reports.series.previousPeriod'),
                            exportPercentLabel: this.localizationService.transform('Reports.percent')
                            + ' - ' + this.localizationService.transform('Reports.series.previousPeriod'),
                            hiddenInLegend: true
                        },
                        {
                            innerRadius: 50,
                            exportLabel: this.reportsChartOptionsService.getLabelByMetric(
                                reportSeries[0].metric, block.displayElement as ChartDisplayElementModel
                            ),
                            exportPercentLabel: this.localizationService.transform('Reports.percent'),
                            hiddenInLegend: false
                        }
                    );
                } else {
                    chartSeries.push({
                        exportLabel: this.reportsChartOptionsService.getLabelByMetric(reportSeries[0].metric, block.displayElement as ChartDisplayElementModel),
                        exportPercentLabel: this.localizationService.transform('Reports.percent'),
                        hiddenInLegend: false
                    });
                }
                break;
            case ChartSpecificTypesEnum.MULTI_STACKED_BAR:
            case ChartSpecificTypesEnum.MULTI_STACKED_COLUMN:
                chartSeries = this.getStackedColumnChartSeriesWithBlock(block);
                break;
        }

        return chartSeries;
    }

    getTopReferralSourcesChartSeries(reportSeries: ReportSeriesModel[], block: BlockModel): ChartSeries[] {
        const metric = reportSeries[0].metric;
        switch (metric) {
            case ExternalChartMetricsEnum.sessions:
            case ExternalChartMetricsEnum.users:
                return [{
                    key: metric,
                    colorMapKey: metric
                }];
            case ExternalChartMetricsEnum.sessionsNewReturning:
                return [
                    {
                        key: ExternalChartMetricsEnum.sessionsNew,
                        label: this.localizationService.transform(`Reports.chartSetupDialog.${ExternalChartMetricsEnum.sessionsNew}`)
                    },
                    {
                        key: ExternalChartMetricsEnum.sessionsReturning,
                        label: this.localizationService.transform(`Reports.chartSetupDialog.${ExternalChartMetricsEnum.sessionsReturning}`)
                    }
                ];
            case ExternalChartMetricsEnum.usersNewReturning:
                return [
                    {
                        key: ExternalChartMetricsEnum.usersNew,
                        label: this.localizationService.transform(`Reports.chartSetupDialog.${ExternalChartMetricsEnum.usersNew}`)
                    },
                    {
                        key: ExternalChartMetricsEnum.usersReturning,
                        label: this.localizationService.transform(`Reports.chartSetupDialog.${ExternalChartMetricsEnum.usersReturning}`)
                    }
                ];
        }
    }

    getReferralTrendChartSeries(reportSeries: ReportSeriesModel[], block: BlockModel): ChartSeries[] {
        const chartSeries = [];
        let existingAxis = -1;
        if (reportSeries[0]?.referralTypes?.length > 0) {
            const s = reportSeries[0];
            const chartSpecificType = (block.displayElement as ChartDisplayElementModel).chartSpecificType
                || reportBlockTypeEnumToDefaultChartSpecificType.get(block.type.key as ReportBlockTypeEnum);
            reportSeries[0].referralTypes.forEach((referralType, index) => {
                const series = {} as ChartSeries;
                series.chartSpecificType = chartSpecificType;
                series.key = referralType;
                series.label = this.localizationService.transform(`Reports.googleAnalyticsCharts.${referralType}`);
                series.exportLabel = this.localizationService.transform(`Reports.googleAnalyticsCharts.${referralType}`);
                series.requiresAxis = series.requiresAxis = index === 0;
                if (series.requiresAxis) {
                    existingAxis += 1;
                }
                series.axisIndex = existingAxis;
                series.colorMapKey = referralType;
                chartSeries.push(series);
            });
        }

        return chartSeries;
    }

    getCoverageImpactTrendChartSeries(reportSeries: ReportSeriesModel[], block: BlockModel): ChartSeries[] {
        const chartSeries = [];
        const series = reportSeries[0];
        const externalSeries = reportSeries[1];
        const chartSpecificType = (block.displayElement as ChartDisplayElementModel).chartSpecificType
            || reportBlockTypeEnumToDefaultChartSpecificType.get(block.type.key as ReportBlockTypeEnum);
        chartSeries.push({
            chartSpecificType: chartSpecificType,
            key: ChartMetricsEnum.volumeOfMentions,
            label: series.label,
            exportLabel: series.label + ' - ' + this.localizationService.transform(`Reports.analysis.type.${series.metric}`),
            requiresAxis: true,
            axisIndex: 0,
            axisTitleText: this.localizationService.transform('Reports.axis.mentions'),
            colorMapKey: 'seriesColor0',
            getSeriesUrl: series.fullSearch ? series.getSocialSeriesUrl.bind(series) : series.getMentionSeriesUrl.bind(series)
        });
        if (externalSeries.referralTypes?.length > 0) {
            const referralTypesOrder = [
                GoogleAnalyticsReportTypesEnum.TOTAL_PAGEVIEWS,
                GoogleAnalyticsReportTypesEnum.SESSIONS,
                GoogleAnalyticsReportTypesEnum.USERS
            ];
            const referralTypes = Array.from(externalSeries.referralTypes).sort((a, b) => {
                return referralTypesOrder.indexOf(a) - referralTypesOrder.indexOf(b);
            });
            referralTypes.forEach((referralType, index) => {
                const s = {} as ChartSeries;
                s.chartSpecificType = chartSpecificType;
                s.key = referralType;
                s.label = this.localizationService.transform(`Reports.googleAnalyticsCharts.${referralType}`);
                s.exportLabel = this.localizationService.transform(`Reports.googleAnalyticsCharts.${referralType}`);
                s.requiresAxis = index === 0;
                s.axisTitleText = index === 0 ? this.localizationService.transform('Reports.axis.traffic') : '';
                s.axisIndex = 1;
                s.hideAxisGrid = true;
                s.colorMapKey = referralType;
                chartSeries.push(s);
            });
        }

        return chartSeries;
    }

    getCoverageMetricChartSeries(reportSeries: ReportSeriesModel): ChartSeries[] {
        const chartSeries = [];
        if (reportSeries.hasPreviousPeriod) {
            chartSeries.push(
                {
                    key: 'previousPeriod',
                    label: 'previous_period',
                    chartSpecificType: ChartSpecificTypesEnum.LINE,
                    requiresAxis: true,
                    axisIndex: 0,
                    strokeDasharray: '4, 3',
                    strokeOpacity: 0.4
                } as ChartSeries,
                {
                    key: 'currentPeriod',
                    label: 'current_period',
                    chartSpecificType: ChartSpecificTypesEnum.LINE,
                    requiresAxis: false,
                    axisIndex: 0
                }
            );
        } else {
            chartSeries.push({
                key: 'currentPeriod',
                label: 'current_period',
                chartSpecificType: ChartSpecificTypesEnum.LINE,
                requiresAxis: true,
                axisIndex: 0
            } as ChartSeries);
        }
        return chartSeries;
    }
}
